import client from '@config/client'
import API_URL from '@config/services'
import { ApiClientResult } from '@/types/api'
import queryString from '@utils/queryString'

/**
 * Interface for API responses
 */

/**
 * Interface for bank payload
 */
interface BankPayload {
  [key: string]: any
}

/**
 * Interface for transaction payload
 */
interface TransactionPayload {
  is_ignored?: boolean
  [key: string]: any
}

/**
 * Interface for transaction match payload
 */
interface MatchPayload {
  [key: string]: any
}

/**
 * Create a new bank
 * @param payload - The bank data
 * @returns Promise with the response
 */
export const createBank = (payload: BankPayload): Promise<ApiClientResult> => {
  return client
    .post(API_URL.BANKS.POST_BANK_LIST_PK, payload)
    .then(response => response)
    .catch(error => error.response)
}

export const patchBank = (
  id: number | string,
  payload: BankPayload
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.BANKS.PATCH_BANK_ID(id), payload)
    .then(response => response)
    .catch(error => error.response)
}

export const deleteBank = (id: number | string): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.BANKS.DELETE_BANK_ID(id))
    .then(response => response)
    .catch(error => error.response)
}

export const connectBank = (
  bankId: number | string,
  redirect_uri: string
): Promise<ApiClientResult> => {
  return client
    .get(
      API_URL.BANKS.CONNECT_BANK(bankId) +
        '?' +
        queryString({
          redirect_uri: redirect_uri
        })
    )
    .then(response => response)
    .catch(error => error.response)
}

export const manageBankConnections = (
  bankId: number | string,
  redirect_uri: string
): Promise<ApiClientResult> => {
  return client
    .get(
      API_URL.BANKS.MANAGE_BANK_CONNECTIONS(bankId) +
        '?' +
        queryString({
          redirect_uri: redirect_uri
        })
    )
    .then(response => response)
    .catch(error => error.response)
}

export const reconnectBank = (
  bankId: number | string,
  redirect_uri: string
): Promise<ApiClientResult> => {
  return client
    .patch(
      API_URL.BANKS.RECONNECT_BANK(bankId) +
        '?' +
        queryString({
          redirect_uri: redirect_uri
        })
    )
    .then(response => response)
    .catch(error => error.response)
}

export const clearConnection = (
  bankId: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.BANKS.DELETE_CONNECTED_USER(bankId))
    .then(response => response)
    .catch(error => error.response)
}

export const forceSync = (
  bankId: number | string
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.BANKS.PATCH_FORCE_SYNC(bankId))
    .then(response => response)
    .catch(error => error.response)
}

export const getTransactionMatches = (): Promise<ApiClientResult> => {
  return client
    .get(API_URL.BANKS.GET_TRANSACTION_MATCHES())
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Ignore a transaction
 * @param transactionId - The transaction ID
 * @param payload - The payload with ignore data
 * @returns Promise with the response
 */
export const ignoreTransaction = (
  transactionId: string | number,
  payload: TransactionPayload
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.CASHFLOW.PATCH_TRANSACTION(transactionId), payload)
    .then(response => response)
    .catch(error => error.response)
}

export const matchInvoiceToTransaction = (
  payload: MatchPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.BANKS.POST_MATCH_TRANSACTION(), payload)
    .then(response => response)
    .catch(error => error.response)
}

export const approveMatch = (
  matchId: number | string,
  payload: MatchPayload
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.BANKS.PATCH_MATCH_TRANSACTION(matchId), payload)
    .then(response => response)
    .catch(error => error.response)
}

export const deleteMatch = (
  matchId: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.BANKS.DELETE_TRANSACTION_MATCHES(matchId))
    .then(response => response)
    .catch(error => error.response)
}
